// ===== Physics engine (shared between game and tests) =====
// Generic: parts, objects and goals come from Registry (the core kit in src/kit/ and the worlds).
// The order bodies are created in drives Matter.js' results (golden): statics, fixed parts, the player's parts, balls,
// goals, objects with phase 'core', other objects, the world's setup.
import type Matter from 'matter-js';
import { Registry } from './registry';
import type { Act, Blocker, GameBody, Level, ObjData, ObjState, PartDef, PhysicsApi, Placement, Rect, SimEvent, SimState, System } from './types';

const W = 1200, H = 800;
const DEG = Math.PI / 180;
type MatterNS = typeof Matter;
type WorldRect = Rect & { a: number };

function partDef(t: string): PartDef | null { return Registry.parts[t] || null; }
function localRects(t: string): Rect[] {
  const p = partDef(t)!;
  if (p.rects) return p.rects;
  if (p.arc) {
    const { r, a0, a1, n, t: th } = p.arc, out: Rect[] = [];
    const step = (a1 - a0) / n;
    for (let i = 0; i < n; i++) {
      const am = (a0 + step * (i + 0.5)) * DEG;
      const seg = 2 * r * Math.sin(step * DEG / 2) + 4;
      // arc under the placement point (bowl shape: a "slide" curve)
      out.push({ x: Math.cos(am) * r, y: Math.sin(am) * r - r * 0.55, w: seg, h: th, a: am / DEG + 90 });
    }
    return out;
  }
  if (p.circle) return [{ x: 0, y: 0, w: p.circle * 2, h: p.circle * 2, circle: true }];
  return [];
}

function worldRects(pl: Placement): WorldRect[] {
  const a = (pl.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a);
  return localRects(pl.t).map(r => ({
    x: pl.x + r.x * c - r.y * s, y: pl.y + r.x * s + r.y * c,
    w: r.w, h: r.h, a: a + (r.a || 0) * DEG, circle: r.circle,
  }));
}

/** Systems with a given hook (in registration order) */
const systems = <K extends keyof System>(k: K) => Registry.systems.filter(s => s[k]) as (System & Required<Pick<System, K>>)[];
/** kind -> handlers from all systems (contact/strike) */
function byKind(k: 'contact' | 'strike') {
  const out = new Map<string, ((api: PhysicsApi, self: GameBody, other: GameBody) => void)[]>();
  for (const s of systems(k)) for (const [kind, fn] of Object.entries(s[k])) { const l = out.get(kind) || []; l.push(fn); out.set(kind, l); }
  return out;
}

function create(Matter: MatterNS, level: Level, placements: Placement[] | null | undefined, opts?: Record<string, unknown>): SimState {
  opts = opts || {};
  const { Engine, Bodies, Body, Composite, Constraint, Events } = Matter;
  const R = (x: number, y: number, w: number, h: number, o?: Matter.IChamferableBodyDefinition) => { const b = Bodies.rectangle(x, y, w, h, o) as GameBody; b.dw = w; b.dh = h; return b; };
  const engine = Engine.create({ enableSleeping: false });
  engine.gravity.y = level.gravity == null ? 1 : level.gravity;
  const world = engine.world;
  const ev: SimEvent[] = [];                      // sound/effect events for the UI
  const S = {
    engine, bodies: [], heroes: [], dyn: [],
    pads: new Set(), paints: new Set(), drags: new Set(), goal: null, events: ev,
    t: 0, won: false, failed: false, inGoal: 0, duckGot: false, still: 0, winT: 0, fx: [],
  } as unknown as SimState;
  const add = <B extends Matter.Body>(b: B) => { Composite.add(world, b); S.bodies.push(b as unknown as GameBody); return b as B & GameBody; };
  // ---- API for parts, objects and goals (kit and worlds) ----
  const hitFns = new Map<GameBody, ((other: GameBody, pair: Matter.Pair) => void)[]>(), actFns: Record<string, ((act: Act) => void)[]> = {};
  S.custom = []; S.things = [];
  const api: PhysicsApi = {
    Matter, Engine, Bodies, Body, Composite, Constraint, engine, world, S, level, DEG, W, H,
    add, R, heroOpts: k => heroOpts(k),
    emit: (e, x, y, extra) => ev.push(Object.assign({ e, x, y }, extra || {})),
    sfx: (name, x, y, col, n) => ev.push({ e: 'sfx', s: name, x, y, col, n }),
    isBall: b => !!b && (b.kind === 'hero' || b.kind === 'helper'),
    isDynamic: b => !!b && !b.isStatic && !b.isSensor,
    onHit(body, fn) { const l = hitFns.get(body) || []; l.push(fn); hitFns.set(body, l); },
    touching(body) { const out: GameBody[] = []; for (const p of engine.pairs.list) { if (!p.isActive) continue; const A = p.bodyA.parent || p.bodyA, B = p.bodyB.parent || p.bodyB; if (A === body) out.push(B); else if (B === body) out.push(A); } return out; },
    onAct(id, fn) { (actFns[id] = actFns[id] || []).push(fn); },
    act: a => doAct(a),
    thing(obj) { S.things.push(obj); return obj; },
    dynamic(b) { S.dyn.push(b); return b; },
    hero(b) { b.kind = 'hero'; S.heroes.push(b); S.dyn.push(b); return b; },
  };
  S.api = api;
  const contact = byKind('contact'), strike = byKind('strike');
  const preStep = systems('preStep'), afterUpdate = systems('afterUpdate'), busy = systems('busy'), actions = systems('act');
  for (const s of systems('init')) s.init(api);

  const heroOpts = (k: string): Matter.IBodyDefinition => ({ restitution: 0.25, friction: 0, frictionStatic: 0, frictionAir: 0.002, density: 0.002, label: k });

  function addPart(pl: Placement, fixed: boolean, idx: number) {
    const t = pl.t, rs = worldRects(pl);
    const pd = partDef(t)!;
    if (pd.create) { const r = pd.create(api, pl, { fixed, idx }); if (r && r.draw) { r.part = pl; S.things.push(r); } return; }
    // default: static rectangles (or circles) from the geometry
    for (const r of rs) {
      const o = { isStatic: true, isSensor: !!pd.sensor, friction: pd.friction ?? 0.03, restitution: pd.restitution ?? 0.2, label: t };
      const b = add(r.circle ? Bodies.circle(r.x, r.y, r.w / 2, o) as GameBody
        : R(r.x, r.y, r.w, r.h, { isStatic: true, angle: r.a, isSensor: o.isSensor, friction: o.friction, restitution: o.restitution, label: t, chamfer: pd.chamfer != null ? { radius: pd.chamfer } : undefined }));
      b.kind = t; b.partIdx = idx; b.fixedPart = fixed; b.plA = (pl.a || 0) * DEG;
      if (pd.frictionStatic != null) b.frictionStatic = pd.frictionStatic;
      if (pd.pad) { S.pads.add(b); b.padPower = pd.padPower || 1; b.padMin = pd.padMin; }
      if (pd.paint) { S.paints.add(b); b.paintK = pd.paintK; b.paintAir = pd.paintAir; }
      const hit = pd.hit;
      if (hit) api.onHit(b, o => hit(api, b, o, pl));
      if (pd.drag != null) { b.drag = pd.drag; S.drags.add(b); }
    }
  }

  // Fixed blocks (tables, shelves, walls)
  for (const s of level.statics || []) {
    const b = add(R(s.x, s.y, s.w, s.h, { isStatic: true, angle: (s.a || 0) * DEG, friction: 0.03, restitution: 0.15, chamfer: { radius: Math.min(6, s.h / 2 - 1, s.w / 2 - 1) }, label: 'static' }));
    b.kind = 'static'; b.skin = s.skin || 'counter'; b.wk = level.wk;
    if (s.friction != null) b.friction = s.friction;
    if (s.restitution != null) b.restitution = s.restitution;
    if (s.sensor) b.isSensor = true;
    if (s.drag != null) { b.drag = s.drag; S.drags.add(b); }
  }
  (level.fixed || []).forEach((pl, i) => addPart(pl, true, i));
  (placements || []).forEach((pl, i) => addPart(pl, false, i));

  // Balls (hero = the theme's ball, helper = e.g. blueberry)
  for (const bdef of level.balls || []) {
    const r = bdef.r || 15;
    const b = add(Bodies.circle(bdef.x, bdef.y, r, Object.assign(heroOpts(bdef.helper ? 'helper' : 'hero'), bdef.opts || {})));
    b.kind = bdef.helper ? 'helper' : 'hero'; b.r = r; b.def = bdef; b.skin = bdef.skin;
    if (bdef.vx || bdef.vy) b.startV = { x: bdef.vx || 0, y: bdef.vy || 0 };
    S.dyn.push(b);
    if (!bdef.helper) S.heroes.push(b);
  }
  const need = (level.goal && level.goal.count) || S.heroes.length;
  S.need = need;

  // Goals
  const g = level.goal!, gd = Registry.goals[g.type];
  if (gd) S.goal = gd.create(api, g);

  // Objects: first the kit's (phase 'core'), then the worlds' – each in the level's order
  for (const phase of ['core', 'world']) {
    for (const o of level.objs || []) {
      const od = Registry.objs[o.t];
      if (!od || (od.phase || 'world') !== phase) continue;
      // the def knows its own data shape (x/y or ObjDataTypes)
      const st = ((od.create ? od.create(api, o as ObjData) : null) || {}) as ObjState;
      st.o = o as ObjData; st.def = od;
      S.custom.push(st);
    }
  }
  // The world's setup for the whole level (e.g. water across the whole level)
  const wdef = level.wk ? Registry.worlds[level.wk] : null;
  if (wdef && wdef.setup) wdef.setup(api, level);

  // Build phase: everything dynamic stays still until you press start
  for (const b of S.dyn) { Body.setStatic(b, true); }
  S.started = false;

  const pendingBounce: { b: GameBody; n: { x: number; y: number }; vin: number; pw: number; min?: number }[] = [], pendingPaint: GameBody[] = [];
  const isDynamic = (b: GameBody) => !b.isStatic && !b.isSensor;
  const root = (b: Matter.Body) => (b.parent || b) as GameBody;

  Events.on(engine, 'collisionStart', (e: Matter.IEventCollision<Matter.Engine>) => {
    for (const p of e.pairs) {
      const A = root(p.bodyA), B = root(p.bodyB);
      if (S.started) { const ha = hitFns.get(A), hb = hitFns.get(B); if (ha) for (const f of ha) f(B, p); if (hb) for (const f of hb) f(A, p); }
      for (const [x, y] of [[A, B], [B, A]]) {
        if (S.pads.has(x) && isDynamic(y) && !y.padImmune) {
          const n = { x: Math.sin(x.plA!), y: -Math.cos(x.plA!) };
          const vn = y.velocity.x * n.x + y.velocity.y * n.y;
          if (vn < 0.5) pendingBounce.push({ b: y, n, vin: -vn, pw: x.padPower || 1, min: x.padMin });
        }
        if (S.paints.has(x) && (y.kind === 'hero' || y.kind === 'helper') && !y.painted) { y.paintK = x.paintK; y.paintAir = x.paintAir; pendingPaint.push(y); }
        const cs = x.kind && contact.get(x.kind);
        if (cs) for (const f of cs) f(api, x, y);
        const ss = y.kind && strike.get(y.kind);
        if (ss) for (const f of ss) f(api, y, x);
        if ((y.kind === 'hero' || y.kind === 'helper') && !x.isSensor && y.speed > 3 && !S.pads.has(x)) ev.push({ e: 'tap', x: y.position.x, y: y.position.y, v: y.speed });
      }
    }
  });

  function doAct(act: Act | null | undefined) {
    if (!act) return;
    if (act.fire) for (const id of ([] as string[]).concat(act.fire)) for (const f of actFns[id] || []) f(act);
    for (const s of actions) for (const [k, f] of Object.entries(s.act)) if (act[k]) f(api, act[k], act);
  }

  Events.on(engine, 'afterUpdate', () => {
    for (const pb of pendingBounce.splice(0)) {
      const v = pb.b.velocity, n = pb.n;
      const vn = v.x * n.x + v.y * n.y;
      const vt = { x: v.x - vn * n.x, y: v.y - vn * n.y };
      const out = Math.max(pb.vin * 0.92 * pb.pw, pb.min != null ? pb.min : 11 * pb.pw);
      Body.setVelocity(pb.b, { x: vt.x + n.x * out, y: vt.y + n.y * out });
      ev.push({ e: 'boing', x: pb.b.position.x, y: pb.b.position.y });
    }
    for (const st of S.custom) if (st.def.after) st.def.after(api, st);
    for (const th of S.things) if (th.after) th.after(api);
    for (const b of pendingPaint.splice(0)) {
      b.painted = true; b.frictionAir = b.paintAir != null ? b.paintAir : 0.035;
      const pk = b.paintK != null ? b.paintK : 0.35;
      Body.setVelocity(b, { x: b.velocity.x * pk, y: b.velocity.y * pk });
      ev.push({ e: 'splat', x: b.position.x, y: b.position.y });
    }
    for (const s of afterUpdate) s.afterUpdate(api);
  });

  function step() {
    if (!S.started) return;
    S.t++;
    for (const s of preStep) s.preStep(api);
    // "brake surface": balls touching it lose speed (friction)
    if (S.drags.size) for (const p of engine.pairs.list) {
      if (!p.isActive) continue;
      const A = root(p.bodyA), B = root(p.bodyB);
      for (const [x, y] of [[A, B], [B, A]]) if (S.drags.has(x) && isDynamic(y)) Body.setVelocity(y, { x: y.velocity.x * x.drag!, y: y.velocity.y * (y.velocity.y < 0 ? x.drag! : 1) });
    }
    for (const st of S.custom) if (st.def.step) st.def.step(api, st);
    for (const th of S.things) if (th.step) th.step(api);
    Engine.update(engine, 1000 / 60);

    if (S.won || S.failed) return;
    // goal
    if (S.goal) Registry.goals[S.goal.type].check(api, S.goal);
    // fail: balls gone
    const out = (b: GameBody) => b.position.y > H + 80 || b.position.y < -160 || b.position.x < -80 || b.position.x > W + 80;
    const alive = S.heroes.filter(h => !out(h)).length;
    if (alive < S.need) { S.failed = 'out'; ev.push({ e: 'fail' }); return; }
    // fail: everything is still
    let moving = false;
    for (const b of S.dyn) if (!b.isStatic && b.speed > 0.06 && !out(b)) { moving = true; break; }
    for (const s of busy) if (s.busy(api)) moving = true;
    for (const st of S.custom) if (st.def.busy && st.def.busy(api, st)) moving = true;
    for (const th of S.things) if (th.busy && th.busy(api)) moving = true;
    S.still = moving ? 0 : S.still + 1;
    if (S.still > 70) { S.failed = 'still'; ev.push({ e: 'fail' }); return; }
    if (S.t > (level.time || 22) * 60) { S.failed = 'time'; ev.push({ e: 'fail' }); }
  }

  function start() {
    if (S.started) return;
    S.started = true;
    for (const b of S.dyn) { Body.setStatic(b, false); if (b.startV) Body.setVelocity(b, b.startV); }
    // reset velocity after setStatic
    for (const b of S.dyn) if (!b.startV) { Body.setVelocity(b, { x: 0, y: 0 }); Body.setAngularVelocity(b, 0); }
    for (const st of S.custom) if (st.def.start) st.def.start(api, st);
    for (const th of S.things) if (th.start) th.start(api);
  }

  S.step = step; S.start = start;
  return S;
}

function simulate(Matter: MatterNS, level: Level, placements: Placement[] | null | undefined, maxSteps?: number) {
  const S = create(Matter, level, placements);
  S.start();
  const lim = maxSteps || 60 * 30;
  while (!S.won && !S.failed && S.t < lim) S.step();
  return { won: S.won, failed: S.failed, t: S.t, duck: S.duckGot, S };
}

function starsFor(level: Level, used: number) {
  const par = level.par as number;
  if (used <= par) return 3;
  if (used <= par + 1) return 2;
  return 1;
}

// ---- Magnet joining: parts with snap click onto the end of shelves and other snap parts ----
function topEnds(x: number, y: number, w: number, h: number, a: number) {
  const c = Math.cos(a), s = Math.sin(a), nx = s * h / 2, ny = -c * h / 2;
  return [{ x: x - c * w / 2 + nx, y: y - s * w / 2 + ny, side: -1 }, { x: x + c * w / 2 + nx, y: y + s * w / 2 + ny, side: 1 }];
}
const snappable = (t: string) => !!(partDef(t) && partDef(t)!.snap);
function snap(level: Level, placements: Placement[], idx: number, R?: number): Placement {
  R = R || 34;
  const pl = placements[idx];
  if (!snappable(pl.t)) return pl;
  const anchors: { x: number; y: number; side: number }[] = [];
  for (const s of level.statics || []) if (s.h <= 40 && s.w > 60) anchors.push(...topEnds(s.x, s.y, s.w, s.h, (s.a || 0) * DEG));
  for (const f of level.fixed || []) if (snappable(f.t)) { const r = worldRects(f)[0]; anchors.push(...topEnds(r.x, r.y, r.w, r.h, r.a)); }
  placements.forEach((p, i) => { if (i !== idx && snappable(p.t)) { const r = worldRects(p)[0]; anchors.push(...topEnds(r.x, r.y, r.w, r.h, r.a)); } });
  const r = worldRects(pl)[0];
  const mine = topEnds(r.x, r.y, r.w, r.h, r.a);
  let best: { d: number; e: typeof mine[0]; an: typeof anchors[0] } | null = null;
  for (const e of mine) for (const an of anchors) {
    if (e.side === an.side) continue;          // left end meets right end
    const d = Math.hypot(e.x - an.x, e.y - an.y);
    if (d < R && (!best || d < best.d)) best = { d, e, an };
  }
  if (!best) return pl;
  // move so the end sits slightly in under the anchor and a touch lower
  const tx = best.an.x - best.e.x + best.e.side * 10, ty = best.an.y - best.e.y + 1;
  return Object.assign({}, pl, { x: Math.round(pl.x + tx), y: Math.round(pl.y + ty), snapped: true });
}

// ---- May the part stand here? (not on top of balls, goals and machines) ----
function blockers(level: Level): Blocker[] {
  const out: Blocker[] = [];
  for (const b of level.balls || []) out.push({ x: b.x, y: b.y, r: (b.r || 15) + 6 });
  const g = level.goal!, gd = Registry.goals[g.type];
  if (gd) out.push(...gd.blockers(g));
  for (const o of level.objs || []) { const od = Registry.objs[o.t]; if (od && od.blockers) out.push(...od.blockers(o as ObjData)); }
  for (const f of level.fixed || []) { const pd = partDef(f.t); if (pd && pd.blockers) out.push(...pd.blockers(f)); }
  return out;
}
function rectCircle(r: WorldRect, c: Blocker) {
  const a = -r.a, dx = c.x - r.x, dy = c.y - r.y;
  const lx = dx * Math.cos(a) - dy * Math.sin(a), ly = dx * Math.sin(a) + dy * Math.cos(a);
  const qx = Math.max(-r.w / 2, Math.min(r.w / 2, lx)), qy = Math.max(-r.h / 2, Math.min(r.h / 2, ly));
  return Math.hypot(lx - qx, ly - qy) < c.r;
}
function valid(level: Level, pl: Placement) {
  if (pl.x < 20 || pl.x > W - 20 || pl.y < 20 || pl.y > H - 20) return false;
  const rs = worldRects(pl), bl = blockers(level);
  for (const r of rs) for (const c of bl) {
    if (r.circle ? Math.hypot(r.x - c.x, r.y - c.y) < r.w / 2 + c.r : rectCircle(r, c)) return false;
  }
  return true;
}

const api = { W, H, partDef, snap, topEnds, valid, blockers, worldRects, localRects, create, simulate, starsFor, DEG };
export const Physics = api;
